/**
 * „Was darf der WhatsApp-Assistent?" je Betrieb (Paul 23.10.2026).
 *
 * Der Katalog kommt vom Backend (`services/whatsapp_funktionen.py` → `wa_funktionen_katalog` im
 * Tenant-Detail) — Portal und Bot nutzen dieselbe Liste. Je Gruppe ein Häkchen „alle an/aus",
 * darunter je Funktion ein Häkchen mit Namen und einzeiliger Erklärung. Gespeichert wird serverseitig
 * nur, was aus ist; neue Funktionen sind damit zunächst an.
 */
export type WaFunktion = { key: string; name: string; erklaerung: string; hinweis?: string }
export type WaFunktionGruppe = { gruppe: string; funktionen: WaFunktion[] }

export function WaFunktionen({ katalog, werte, onChange, deaktiviert }: {
  katalog: WaFunktionGruppe[]
  /** key → an/aus; fehlt ein Key, gilt er als an */
  werte: Record<string, boolean>
  onChange: (werte: Record<string, boolean>) => void
  /** Assistent insgesamt aus → alles ausgegraut */
  deaktiviert?: boolean
}) {
  const an = (key: string) => werte[key] !== false
  const setze = (keys: string[], wert: boolean) => {
    const neu = { ...werte }
    for (const k of keys) neu[k] = wert
    onChange(neu)
  }
  if (!katalog.length) return null
  return (
    <div data-testid="wa-funktionen" style={{ opacity: deaktiviert ? 0.5 : 1 }}>
      {deaktiviert && (
        <div className="text-[11px] text-muted-foreground mb-2" data-testid="wa-funktionen-aus">
          Der WhatsApp-Assistent ist für diesen Betrieb aus — erst oben einschalten.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {katalog.map((g) => {
          const keys = g.funktionen.map((f) => f.key)
          const alle = keys.every(an)
          const einige = keys.some(an)
          return (
            <div key={g.gruppe} className="rounded-md border" style={{ padding: "8px 10px" }}
              data-testid={`wa-gruppe-${g.gruppe}`}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: deaktiviert ? "default" : "pointer",
                fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={alle}
                  disabled={deaktiviert}
                  ref={(el) => { if (el) el.indeterminate = einige && !alle }}
                  onChange={() => setze(keys, !alle)}
                  aria-label={`${g.gruppe}: alle an/aus`}
                  data-testid={`wa-gruppe-alle-${g.gruppe}`}
                />
                <span>{g.gruppe}</span>
              </label>
              {g.funktionen.map((f) => (
                <label key={f.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "3px 0 3px 18px",
                  cursor: deaktiviert ? "default" : "pointer" }}>
                  <input
                    type="checkbox"
                    checked={an(f.key)}
                    disabled={deaktiviert}
                    onChange={(e) => setze([f.key], e.target.checked)}
                    style={{ marginTop: 3 }}
                    data-testid={`wa-funktion-${f.key}`}
                  />
                  <span>
                    <span style={{ fontSize: 13 }}>{f.name}</span>
                    <span className="block text-[10.5px] text-muted-foreground">{f.erklaerung}</span>
                    {f.hinweis && <span className="block text-[10.5px] text-muted-foreground">✉️ {f.hinweis}</span>}
                  </span>
                </label>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
